import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import _ from 'lodash'
import { api, ApplicationType, sendAsync } from '../../app/applicationSlice'
import { RootState, RootThunk, RootThunkApi } from '../../app/store'
import { constructMenuLayers, fromJson, Menu } from '../../models/menu'
import {
  getAvailableMenuList as apiGetMenuList,
  getAvailableMenuLists as apiGetMenuLists,
  getAvailableThreeMenuLists as apiGetThreeMenuLists,
  getGrantedMenuList as apiGetGrantedMenuList,
  getGrantedThreeMenuList as apiGetGrantedThreeMenuList,
  getTenantAvailableMenuList,
  getUserGrantedMenuList,
  grantMenus as apiGrantMenus,
  grantThreeMenus as apiThreeGrantMenus,
  grantUserMenus,
  thirdTenantMenuTreeFn,
  setThirdTenantMenuTreeFn,
  thirdMemberMenuTreeFn,
  setThirdMemberMenuTreeFn
} from '../../services/menu'

interface GrantState {
  menus: Menu[]
  twoMenus: Menu[]
  granted: string[]
  grantedTwo: string[]
  imageBast: string
}

const initialState: GrantState = {
  menus: [],
  twoMenus: [],
  granted: [],
  grantedTwo: [],
  imageBast: '',
}

const grantSlice = createSlice({
  name: 'grant',
  initialState,
  reducers: {
    setMenus: (state, action: PayloadAction<Menu[]>) => {
      state.menus = action.payload
    },
    setTwoMenus: (state, action: PayloadAction<Menu[]>) => {
      state.twoMenus = action.payload
    },
    setGrantedMenus: (state, action: PayloadAction<string[]>) => {
      state.granted = action.payload
    },
    setGrantedTwoMenus: (state, action: PayloadAction<string[]>) => {
      state.grantedTwo = action.payload
    },
    clearMenus: (state) => {
      state.menus = []
      state.granted = []
      state.grantedTwo = []
    },
  },
})

const {
  setMenus,
  setTwoMenus,
  setGrantedMenus,
  setGrantedTwoMenus,
} = grantSlice.actions

export const { clearMenus } = grantSlice.actions

export function getMenuList(
  type: ApplicationType,
  str?: string,
  tenantId?: string
): RootThunk {
  return api(
    type === ApplicationType.Management
      ? apiGetMenuList(str, tenantId)
      : getTenantAvailableMenuList(),
    (data: Record<string, never>, disptach) => {
      const menus = constructMenuLayers(data.records)
      disptach(setMenus(menus))
    }
  )
}

export function getMenuLists(
  state: ApplicationType,
  str?: string,
  tenantId?: string,
  type?: any
): RootThunk {
  return api(
    state === ApplicationType.Management
      ? apiGetMenuLists(str, tenantId, type)
      : getTenantAvailableMenuList(),
    (data: Record<string, never>, disptach) => {
      const menus = constructMenuLayers(data.records)
      disptach(setMenus(menus))
    }
  )
}

// 三级菜单全部列表
export function getThreeMenuLists(
  state: ApplicationType,
  str?: string,
  tenantId?: string,
  type?: any
): RootThunk {
  return api(
    state === ApplicationType.Management
      ? apiGetThreeMenuLists(str, tenantId, type)
      : getTenantAvailableMenuList(),
    (data :Record<string, never>, disptach) => {
      const menus = constructMenuLayers(data as any)
      disptach(setTwoMenus(menus))
    }
  )
}

export function getGrantedMenuList(
  type: ApplicationType,
  tenantId?: string,
  userId?: string
): RootThunk {
  return api(
    type === ApplicationType.Management
      ? apiGetGrantedMenuList(tenantId as string, userId)
      : getUserGrantedMenuList(userId),
    (data: Record<string, never>, disptach) => {
      const menus = _.chain(data.records)
        .map((m) => fromJson(m).id)
        .value()
      disptach(setGrantedMenus(menus))
    }
  )
}

// 三级菜单选中
export function getGrantedThreeMenuList(
  type: ApplicationType,
  tenantId?: string
): RootThunk {
  return api(
    apiGetGrantedThreeMenuList(tenantId as string),
    (data: Record<string, never>, disptach) => {
      const menus = _.chain(data)
        .map((m) => fromJson(m).menuId as string)
        .value()?.map((v)=>{return v?.toString()})
        console.log(menus, 'menus')
        
      disptach(setGrantedTwoMenus(menus))
    }
  )
}

export function grantMenus(
  type: ApplicationType,
  tenantId: string | undefined,
  userId: string | undefined,
  menuIds: string[]
): RootThunk {
  return api(
    type === ApplicationType.Management
      ? apiGrantMenus(tenantId as string, userId, menuIds)
      : grantUserMenus(userId as string, menuIds),
    (_, dispatch) => {
      dispatch(getGrantedMenuList(type, tenantId, userId))
    }
  )
}

// 三级菜单保存
export function grantThreeMenus(
  tenantId: string | undefined,
  menuIds: string[]
): RootThunk {
  return api(apiThreeGrantMenus(tenantId as string, menuIds), (_, dispatch) => {
    dispatch(getGrantedThreeMenuList(0, tenantId))
  })
}

// 机构菜单权限tree
export const getThirdTenantMenuTree = createAsyncThunk<
  void,
  any,
  RootThunkApi<void>
>("user/thirdTenantMenuTree", async (params, api) => {
  return sendAsync(thirdTenantMenuTreeFn(params), api);
});

// 机构菜单权限tree修改
export const setThirdTenantMenuTree = createAsyncThunk<
  void,
  any,
  RootThunkApi<void>
>("user/setThirdTenantMenuTree", async (params, api) => {
  return sendAsync(setThirdTenantMenuTreeFn(params), api);
});

// 员工菜单权限tree
export const getThirdMemberMenuTree = createAsyncThunk<
  void,
  any,
  RootThunkApi<void>
>("user/thirdTenantMenuTree", async (params, api) => {
  return sendAsync(thirdMemberMenuTreeFn(params), api);
});

// 员工菜单权限tree修改
export const setThirdMemberMenuTree = createAsyncThunk<
  void,
  any,
  RootThunkApi<void>
>("user/setThirdTenantMenuTree", async (params, api) => {
  return sendAsync(setThirdMemberMenuTreeFn(params), api);
});

export const selectMenus = (state: RootState): Menu[] => state.grant.menus

export const selectTwoMenus = (state: RootState): Menu[] => state.grant.twoMenus

export const selectGrantedMenus = (state: RootState): string[] =>
  state.grant.granted

export const selectTwoGrantedMenus = (state: RootState): string[] =>
  state.grant.grantedTwo
  
export default grantSlice.reducer
